import api from "@/api";
import moment from "moment-timezone";

import { getStoreName, setDocumentTitle } from "@/util.js";
import { setJsonLd } from "@/jsonld.js";

import { formatServiceDates, modifyTodayForCurrentMoment } from "@/helper";

/*
const setDefaultWaitTimeIfAPIReturnsZeroOrSixty = (serviceType, waitTime) => {
  const DEFAULT_DELIVERY_MINUTES = 40;
  const DEFAULT_PICKUP_MINUTES = 20;

  return waitTime > 0 && waitTime < 60
    ? waitTime
    : serviceType === SERVICE_TYPE.DELIVERY
    ? DEFAULT_DELIVERY_MINUTES
    : DEFAULT_PICKUP_MINUTES;
};
*/

const state = () => ({
  info: null,
  subscribed: "PENDING",
});

const actions = {
  calculateStars({ commit, state, rootState }) {
    if (state.info.coffee_clubs.totals && rootState.cart.products) {
      let totals = state.info.coffee_clubs.totals.map((t) => {
        let products = rootState.cart.products.filter(
          (p) => p.club_id == t.club_id,
        );
        let qty = products.reduce((i, p) => i + p.qty, 0);
        return {
          ...t,
          free_products_qty: products.length
            ? t.free_products_qty + qty
            : t.free_products_qty,
        };
      });

      commit("UPDATE_CLUB_TOTALS", totals);
    }
  },
  async fetch({ commit }) {
    const data = await api({ url: `/123/stores/name/${getStoreName()}` });
    const subscribed = data.info;
    data.err
      ? commit("error/SHOW_ERROR", data.err, { root: true })
      : subscribed
      ? (commit("UPDATE", data.info),
        commit(
          "departments/UPDATE_MENU_AS_FEATURED",
          data.info.menu_as_featured,
          { root: true },
        ))
      : commit("REDIRECT");
  },
  async fetchPoints({ commit }) {
    const data = await api({ url: "/123/point_offers/available_products" });
    const points = data.info;
    data.err
      ? commit("error/SHOW_ERROR", data.err, { root: true })
      : commit("UPDATE_POINTS", points);
  },
  async fetchClubs({ commit }) {
    const data = await api({ url: "/123/coffee_clubs/available_products" });
    const clubs = data.info;
    data.err
      ? commit("error/SHOW_ERROR", data.err, { root: true })
      : commit("UPDATE_CLUBS", clubs);
  },

  // This function gets the current stores delivery days and wait time
  async fetchAndModifyService(
    { commit, state, rootGetters, rootState },
    { serviceNow },
  ) {
    // Check if delivery order
    const serviceIsDelivery =
      rootGetters["orders/serviceIsDelivery"] === "DELIVERY";

    // Create summary of pickup and/or delivery services
    let service = {
      days: serviceIsDelivery
        ? state.info.delivery_days
        : state.info.pickup_days,
      now: serviceNow,
    };

    if (!serviceIsDelivery) {
      service.waitTime = state.info.pickup_time_mins;
    } else {
      service.waitTime =
        rootState.cart.deliveryDuration || state.info.delivery_time_mins;
    }

    commit("UPDATE_SERVICE", service);
  },
  setPointsProductbyUpc({ state, dispatch }, { offerId, upc }) {
    let offer = state.info.points_offers.find((o) => o.offering_id === offerId);
    if (offer) {
      let product;
      if (offer.xtra.paywithpoints) {
        product = {
          name: offer.xtra.name,
          paywithpoints: offer.xtra.paywithpoints,
          points: offer.points,
        };
      } else {
        product = offer?.xtra.products.find((o) => o.upc_encoded === upc);
      }
      if (product) {
        dispatch(
          "product/setCurrentProductDetails",
          {
            ...product,
            redeemable: product.points >= (state.info.points.total_points || 0),
            loyalty: true,
            redemption_points_offer: offer.points,
            redemption_points_offer_id: offerId,
          },
          { root: true },
        );
      }
    }
  },
  setClubProductbyUpc({ dispatch, getters }, { offerId, upc }) {
    let offer = getters.clubs.find(
      (c) => c.club_id === offerId && c.free_products_qty > 0,
    );
    if (offer) {
      let product = offer.products.find((o) => o.upc_encoded === upc);
      if (product) {
        dispatch(
          "product/setCurrentProductDetails",
          {
            ...product,
            redeemable: product.is_redemption,
            loyalty: true,
          },
          { root: true },
        );
      }
    }
  },
  async update_pickup_days({ commit }, pickup_days) {
    commit("UPDATE_PICKUP_DAYS", pickup_days);
  },
  async update_pickup_time_mins({ commit }, pickup_estimate) {
    commit("UPDATE_PICKUP_TIME_MINS", pickup_estimate);
  },
  async update_delivery_amount({ commit }, delivery_amount) {
    commit("UPDATE_DELIVERY_AMOUNT", delivery_amount);
  },
  async update_delivery_name({ commit }, delivery_name) {
    commit("UPDATE_DELIVERY_NAME", delivery_name);
  },
  async update_delivery_days({ commit }, delivery_days) {
    commit("UPDATE_DELIVERY_DAYS", delivery_days);
  },
};

const mutations = {
  REDIRECT(state) {
    state.subscribed = "NO";
  },
  UPDATE(state, info) {
    const initStoreInfo = {
      hours: [],
      hoursLt: [],
      hoursLtIso: [],
    };
    const store = {
      ...info,
      ...(!info.image && {
        image: require("@/assets/banner/store_placeholder.png"),
      }),
      store_info: {
        ...initStoreInfo,
        ...info.store_info,
      },
    };
    moment.tz.setDefault(store.tz);

    setDocumentTitle(store.name);
    const productionEnv = process.env.NODE_ENV === "production";
    productionEnv && setJsonLd("convenienceStore", store);
    state.subscribed = "YES";
    state.info = store;
  },

  UPDATE_SERVICE(state, service) {
    const { days, now, waitTime } = service;
    if (!days.length) {
      state.info = { ...state.info, serviceDays: [] };
      return;
    }
    const currentMoment = moment();
    // Check if order is for today
    const serviceToday = moment(currentMoment.format("YYYY-MM-DD")).isSame(
      days[0].date,
    );

    /*
     * if service today, any times still available?
     * add pickup or delivery time_mins to time now,
     * get next service (pickup or delivery) time
     * (add time_mins and get next 15 minute interval)
     * and see that it's within the end time
     */
    const picks = serviceToday
      ? modifyTodayForCurrentMoment(currentMoment, days, now, waitTime)
      : days;

    state.info = {
      ...state.info,
      serviceDays: formatServiceDates(picks, waitTime),
      storeStartMoment: moment(days[0].iso.start) || null,
      // waitTime: setDefaultWaitTimeIfAPIReturnsZeroOrSixty(type, waitTime),
      waitTime,
    };
  },

  UPDATE_CLUB_TOTALS(state, totals) {
    state.info.coffee_clubs.totals = totals;
  },

  UPDATE_PICKUP_DAYS(state, days) {
    state.info.pickup_days = days;
  },

  UPDATE_PICKUP_TIME_MINS(state, mins) {
    state.info.pickup_time_mins = mins;
  },

  UPDATE_DELIVERY_AMOUNT(state, amount) {
    state.info.delivery_amount = amount;
  },
  UPDATE_DELIVERY_NAME(state, name) {
    state.info.delivery_name = name;
  },
  UPDATE_DELIVERY_DAYS(state, days) {
    state.info.delivery_days = days;
  },
  UPDATE_POINTS(state, points) {
    state.info.points = points.points;
    state.info.points_offers = points.offers;
  },
  UPDATE_CLUBS(state, clubs) {
    state.info.coffee_clubs = clubs;
  },
};

const getters = {
  canada: (state) => state.info.address.country === "CA",
  clubs: (state) => {
    let clubs = state.info.coffee_clubs.totals
      .map((t) => {
        let products;
        state.info.coffee_clubs.products_by_club.forEach((p) => {
          if (p.club_id == t.club_id) {
            products = p.products.map((e) => {
              return {
                ...e,
                is_redemption: t.free_products_qty > 0,
                club_id: t.club_id,
              };
            });
          }
        });

        return { ...t, products };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    return clubs.filter((c) => c.products?.length);
  },
  club_free_products_qty_remaining:
    (state, getters, rootState) => (club_id) => {
      if (rootState.cart.products) {
        let clubTotal = state.info.coffee_clubs.totals.find(
          (t) => t.club_id === club_id,
        );
        let redemtionItems = rootState.cart.products.filter(
          (p) => p.is_redemption && p.club_id === club_id,
        );

        if (redemtionItems.length) {
          return redemtionItems.reduce((t, p) => {
            return t - p.qty;
          }, clubTotal.free_products_qty);
        }
        return clubTotal?.free_products_qty;
      }
    },
  getPointsOffer: (state) => (offerId) => {
    return state.info.points_offers.find((o) => o.offering_id === offerId);
  },
  hasClubs: (state, getters) => {
    return !!getters.clubs.length;
  },
  hasPoints: (state) => {
    return !!state.info.points_offers.length;
  },
  rewardsFormat: (state) => {
    return (
      state.info.store_info.customization?.website.style.website_format ===
      "store_info_rewards"
    );
  },
  rewardsAvailable: (state, getters) => {
    return (
      getters.total_points_remaining ||
      getters.clubs.filter((c) => c.free_products_qty > 0).length
    );
  },
  points_offers: (state) => {
    return state.info.points_offers
      .filter(
        (o) => o.active && (o.xtra.products.length || o.xtra.paywithpoints),
      )
      .sort(
        (a, b) => a.points - b.points || a.xtra.name.localeCompare(b.xtra.name),
      );
  },
  getStoreId: (state) => state.info?.store_id,
  total_points_remaining: (state, getters, rootState) => {
    if (
      state.info.customization?.website.style.website_format ==
      "store_info_rewards"
    ) {
      return state.info.points.total_points;
    }
    if (rootState.cart.products && state.info.points) {
      return rootState.cart.products.reduce((t, p) => {
        return p.redemption_points_offer_id
          ? t - p.redemption_points_offer * p.qty
          : t;
      }, state.info.points.total_points);
    }
    return 0;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
